import { useState } from 'react';
import { CopyButton } from './CopyButton';

/**
 * The connector, offered rather than explained: per-client instructions, the
 * URL or command to paste, the two scopes named, and prompts naming the
 * user's own device.
 *
 * Every interpolated value here is user-set text rendered through React, so
 * it is escaped by construction. Nothing in this panel may reach
 * dangerouslySetInnerHTML.
 */
const PANEL_ID = 'connect-panel-content';

// The OAuth client ID MCP clients must sign in with.
const CLIENT_ID = 'zafron-mcp';

// Per-browser convenience only: losing it just shows the setup again.
const HIDDEN_KEY = 'zafron.dashboards.setupHidden';
const readHidden = () => {
  try { return window.localStorage.getItem(HIDDEN_KEY) === '1'; } catch (e) { return false; }
};
const writeHidden = (hidden) => {
  try {
    if (hidden) window.localStorage.setItem(HIDDEN_KEY, '1');
    else window.localStorage.removeItem(HIDDEN_KEY);
  } catch (e) { /* storage blocked; the toggle still works for this visit */ }
};

export function ConnectPanel({ deviceName, defaultOpen = false }) {
  const [open, setOpen] = useState(() => defaultOpen || !readHidden());
  const [client, setClient] = useState('claude');

  const base = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const connector = `${base}/mcp`;
  const subject = deviceName || 'my device';

  const clients = [
    {
      id: 'claude',
      label: 'Claude',
      help: 'In Claude, add a custom connector, paste the server URL, and under Advanced settings set the OAuth Client ID.',
      value: connector,
      what: 'the server URL',
      needsClientId: true,
    },
    {
      id: 'claude-code',
      label: 'Claude Code',
      help: 'Run this once in your terminal, then run /mcp inside Claude Code to sign in.',
      value: `claude mcp add --transport http --client-id ${CLIENT_ID} zafron ${connector}`,
      what: 'the command',
    },
    {
      id: 'chatgpt',
      label: 'ChatGPT',
      help: 'In ChatGPT, turn on Developer mode (Settings → Connectors → Advanced), then create a connector: paste the server URL and choose OAuth. If it asks for OAuth credentials, enter the client ID and leave the secret blank.',
      value: connector,
      what: 'the server URL',
      needsClientId: true,
    },
    {
      id: 'grok',
      label: 'Grok',
      help: 'At grok.com/connectors, choose New Connector, then Custom, and paste the server URL. When it asks for OAuth credentials, enter the client ID and leave the secret blank.',
      value: connector,
      what: 'the server URL',
      needsClientId: true,
    },
    {
      id: 'muse',
      label: 'Muse',
      help: 'Muse has no connector menu; send it this message and it builds the connector, then asks you to sign in.',
      value: `Create a connector for Zafron using the MCP server at ${connector} over streamable HTTP, signing in with OAuth client ID ${CLIENT_ID}.`,
      what: 'the message',
    },
    {
      id: 'other',
      label: 'Other MCP client',
      help: 'Add a remote MCP server over streamable HTTP, point it at this URL, and set the OAuth client ID.',
      value: connector,
      what: 'the server URL',
      needsClientId: true,
    },
  ];
  const active = clients.find((c) => c.id === client);

  const prompts = [
    `Build a dashboard for ${subject} with temperature and humidity over the last week`,
    'Add a battery gauge to my latest dashboard',
    'Show me when temperature went above 25 °C today',
  ];

  const toggle = () => {
    writeHidden(open);
    setOpen(!open);
  };

  return (
    <section
      id="connect"
      aria-labelledby="connect-title"
      className="scroll-mt-6 rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
    >
      <div className="flex items-start justify-between gap-4 px-6 py-6 sm:px-10">
        <div>
          <h2 id="connect-title" className="text-xl font-semibold text-gray-900 dark:text-gray-50">
            Author dashboards from your AI assistant
          </h2>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Connect Zafron as an MCP server, then describe the dashboard you want in chat.
          </p>
        </div>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={PANEL_ID}
          onClick={toggle}
          className="shrink-0 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-50 dark:hover:bg-gray-800"
        >
          {open ? 'Hide setup' : 'Show setup'}
        </button>
      </div>

      {open && (
        <div
          id={PANEL_ID}
          className="grid gap-10 border-t border-gray-200 px-6 py-8 sm:px-10 lg:grid-cols-2 dark:border-gray-800"
        >
          <div>
            <Step n={1}>Add Zafron to your client</Step>

            <div
              role="tablist"
              aria-label="MCP client"
              className="mt-5 inline-flex flex-wrap rounded-lg border border-gray-200 bg-gray-100 p-1 dark:border-gray-800 dark:bg-gray-800"
            >
              {clients.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  id={`client-tab-${c.id}`}
                  aria-selected={c.id === client}
                  aria-controls="client-panel"
                  onClick={() => setClient(c.id)}
                  className={
                    'rounded-md px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ' +
                    (c.id === client
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-50'
                      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200')
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div id="client-panel" role="tabpanel" aria-labelledby={`client-tab-${client}`}>
              <p className="mt-4 text-gray-500 dark:text-gray-400">{active.help}</p>
              <pre className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-[#efefeb] px-4 py-4 text-sm text-gray-900 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-50">
                <code>{active.value}</code>
              </pre>
              <div className="mt-3 flex items-start justify-between gap-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Sign in with your Zafron account when your client asks, and grant
                  both <strong className="font-medium text-gray-700 dark:text-gray-300">mcp:read</strong> and{' '}
                  <strong className="font-medium text-gray-700 dark:text-gray-300">mcp:dashboards</strong>.
                  The second is what separates drafting from saving.
                </p>
                <CopyButton text={active.value} what={active.what} />
              </div>
              {active.needsClientId && (
                <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    OAuth Client ID{' '}
                    <code className="ml-1 rounded bg-[#efefeb] px-2 py-0.5 text-gray-900 dark:bg-gray-800 dark:text-gray-50">{CLIENT_ID}</code>
                  </p>
                  <CopyButton text={CLIENT_ID} what="the client ID" />
                </div>
              )}
            </div>
          </div>

          <div>
            <Step n={2}>Ask for a dashboard</Step>
            <ul className="mt-5 space-y-3">
              {prompts.map((p, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-5 py-4 dark:border-gray-800"
                >
                  <p className="text-gray-900 dark:text-gray-50">&ldquo;{p}&rdquo;</p>
                  <CopyButton text={p} what={`prompt ${i + 1}`} />
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              New dashboards appear on this page as soon as your assistant saves them.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function Step({ n, children }) {
  return (
    <h3 className="flex items-center gap-3 font-semibold text-gray-900 dark:text-gray-50">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-900 text-sm text-white dark:bg-gray-700">
        {n}
      </span>
      {children}
    </h3>
  );
}
